import { useState } from 'react';

import { Select } from '@/components/ui/Select';
import type { Option } from '@/constants/institute';
import { cn } from '@/lib/utils';
import type { StaffMember } from '@/types/staff';

/** Enough to recognise the role at a glance; the rest are one click away. */
const PREVIEW_COUNT = 5;

export interface RoleHoldersProps {
  /** The role this card is for. */
  roleUuid: string;
  roleName: string;
  holders: StaffMember[];
  /** Every role a holder may be moved to, this one included. */
  roleOptions: Option[];
  /**
   * Whether this card offers a move at all. Off for the Administrator role —
   * that account is the school's own, and the server refuses to change it.
   */
  canChange: boolean;
  /** The signed-in account, which the server will not let change its own role. */
  selfUuid?: string;
  /** The member whose move is being saved, so their picker can wait. */
  savingUuid: string | null;
  onMove: (member: StaffMember, roleUuid: string) => void;
}

/**
 * Who holds a role, with a picker to move each of them to another.
 *
 * The move itself is the page's: it asks for confirmation first, as a role
 * change grants and takes away access the moment it is saved.
 */
export function RoleHolders({
  roleUuid,
  roleName,
  holders,
  roleOptions,
  canChange,
  selfUuid,
  savingUuid,
  onMove,
}: RoleHoldersProps) {
  const [showAll, setShowAll] = useState(false);
  const shown = showAll ? holders : holders.slice(0, PREVIEW_COUNT);
  const hidden = holders.length - shown.length;

  if (holders.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      <p className="text-[11px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">
        Held by
      </p>
      <ul className="flex flex-col divide-y divide-slate-200/80 dark:divide-slate-800 rounded-2xl border border-slate-200/80 dark:border-slate-800">
        {shown.map((member) => {
          // The server refuses both: your own role, and the school owner's.
          const isLocked = member.uuid === selfUuid || member.isOwner;
          const isActive = member.status === 'ACTIVE';

          return (
            <li
              key={member.uuid}
              className="flex flex-col gap-2 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex min-w-0 items-center gap-2.5">
                <span
                  aria-hidden
                  className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-blue-50 dark:bg-blue-950/50 text-[11px] font-black text-blue-700 dark:text-blue-300"
                >
                  {initialsOf(member)}
                </span>
                <div className="min-w-0">
                  <p className="flex items-center gap-1.5 truncate text-sm font-bold text-slate-900 dark:text-white">
                    <span className="truncate">{member.fullName}</span>
                    {!isActive && (
                      <span className="shrink-0 rounded-full border border-amber-200 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-950/50 px-1.5 py-0.5 text-[10px] font-black uppercase tracking-wider text-amber-700 dark:text-amber-300">
                        Suspended
                      </span>
                    )}
                  </p>
                  <p className="truncate text-xs font-medium text-slate-500 dark:text-slate-400">
                    {[member.designation, member.employeeCode].filter(Boolean).join(' · ') || member.email}
                  </p>
                </div>
              </div>

              {canChange && !isLocked && (
                <div className="w-full sm:w-44 sm:shrink-0">
                  <Select
                    label={`Role for ${member.fullName}`}
                    hideLabel
                    hideSearch={roleOptions.length < 8}
                    options={roleOptions}
                    value={roleUuid}
                    disabled={savingUuid === member.uuid}
                    onChange={(event) => {
                      if (event.target.value !== roleUuid) onMove(member, event.target.value);
                    }}
                  />
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {holders.length > PREVIEW_COUNT && (
        <button
          type="button"
          onClick={() => setShowAll((value) => !value)}
          className={cn(
            'self-start rounded-lg px-1 text-xs font-bold text-blue-700 dark:text-blue-300 hover:underline',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
          )}
          aria-label={showAll ? `Show fewer people with ${roleName}` : `Show all ${holders.length} people with ${roleName}`}
        >
          {showAll ? 'Show fewer' : `Show ${hidden} more`}
        </button>
      )}
    </div>
  );
}

function initialsOf(member: StaffMember): string {
  return [member.firstName, member.lastName]
    .map((part) => part?.trim().charAt(0) ?? '')
    .join('')
    .toUpperCase();
}
